import React, { useEffect, useRef, useState } from "react";

/* ==========================================================================
   Tracking
   ========================================================================== */

function track(event, data = {}) {
  const payload = { event, ...data };
  console.log("[uta]", payload);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

// Module-scope handler so memoized phone components receive zero props and
// never re-render (Ringba's swapped number must survive the app's lifetime).
function onCallTap() {
  track("call_button_tap");
}

/* ==========================================================================
   Phone number — Ringba replacement anchor
   --------------------------------------------------------------------------
   (800) 730-6512 is a hardcoded literal rendered as ONE unbroken text node.
   Ringba finds and replaces that exact string in the DOM at runtime. Never
   split it across elements, derive it from state, or reformat it. Every
   component that renders it is React.memo'd with no props, so a re-render
   can never restore the hardcoded value after Ringba swaps it.
   ========================================================================== */

const TEL_HREF = "tel:+18007306512";

/* Failsafe href sync — belt-and-suspenders for Ringba.
   Ringba's tag normally rewrites both visible numbers and tel: hrefs. But the
   result screen (and all three tel links) mounts AFTER page load. If Ringba
   swaps the visible number text but misses any late-mounted href, a tap would
   dial the unswapped anchor number and the call would not attribute. This
   observer watches for the visible swap and copies the pool number into every
   tel: href. It never touches visible text (that stays Ringba's job) and is a
   no-op when Ringba has already rewritten the hrefs itself. */
const HARDCODED_DIGITS = "18007306512";

function digitsOf(s) {
  return ((s || "").match(/\d+/g) || []).join("");
}

function syncTelHrefs() {
  const anchors = Array.from(document.querySelectorAll('a[href^="tel:"]'));
  const swapped = anchors.find((a) => {
    const d = digitsOf(a.textContent);
    return d.length >= 10 && d !== HARDCODED_DIGITS && "1" + d !== HARDCODED_DIGITS;
  });
  if (!swapped) return;
  let pool = digitsOf(swapped.textContent);
  if (pool.length === 10) pool = "1" + pool;
  const poolHref = "tel:+" + pool;
  anchors.forEach((a) => {
    if (digitsOf(a.getAttribute("href")) !== pool) {
      a.setAttribute("href", poolHref);
    }
  });
}

function installTelSync() {
  const observer = new MutationObserver(syncTelHrefs);
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    characterData: true,
  });
  syncTelHrefs();
  return () => observer.disconnect();
}

const PhoneNumberLink = React.memo(function PhoneNumberLink() {
  return (
    <a
      href={TEL_HREF}
      onClick={onCallTap}
      className="font-serif text-[27px] font-bold tracking-wide text-navy"
    >
      (800) 730-6512
    </a>
  );
});

const PrimaryCallButton = React.memo(function PrimaryCallButton() {
  return (
    <a
      href={TEL_HREF}
      onClick={onCallTap}
      className="block w-full rounded-xl bg-gold px-6 py-4 text-center text-[17px] font-bold text-navy shadow-[0_8px_24px_rgba(201,162,39,0.45)] ring-1 ring-[#b08d1e] transition-transform duration-150 active:scale-[0.98]"
    >
      📞 Find Out Where You Stand — Free Call
    </a>
  );
});

const StickyCallBar = React.memo(function StickyCallBar() {
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-[440px] border-t-2 border-gold/70 bg-navy px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-6px_24px_rgba(11,37,69,0.35)] sm:rounded-t-xl">
      <a
        href={TEL_HREF}
        onClick={onCallTap}
        className="block w-full rounded-xl bg-gold px-4 py-3.5 text-center text-base font-bold text-navy ring-1 ring-[#b08d1e]"
      >
        📞 Free, Confidential Call — Tap to Dial
      </a>
    </div>
  );
});

/* ==========================================================================
   Quiz content
   ========================================================================== */

const Q1_OPTIONS = [
  { id: "under_5k", label: "Under $5,000" },
  { id: "5k_10k", label: "$5,000 – $10,000" },
  { id: "10k_50k", label: "$10,000 – $50,000" },
  { id: "over_50k", label: "Over $50,000" },
  { id: "not_sure", label: "I'm not sure — I haven't looked in a while" },
];

const Q2_OPTIONS = [
  { id: "letters", label: "IRS letters keep coming" },
  {
    id: "garnishment",
    label: "My wages or bank account are being garnished (or about to be)",
  },
  { id: "not_filed", label: "It's been years since I filed" },
  { id: "off_my_back", label: "I just want this off my back" },
];

// "Not sure" is intentionally treated like the $10K+ path — it captures avoiders.
const Q1_RESULT_LINES = {
  "10k_50k":
    "Taxpayers in your situation often qualify for the strongest relief options — including settling for less than what's owed.",
  over_50k:
    "Taxpayers in your situation often qualify for the strongest relief options — including settling for less than what's owed.",
  not_sure:
    "Taxpayers in your situation often qualify for the strongest relief options — including settling for less than what's owed.",
  "5k_10k":
    "You may qualify to stop penalties and interest from growing — and resolve this for good.",
  under_5k:
    "You may have options to stop penalties from growing before this gets bigger.",
};

const Q2_EMPATHY_LINES = {
  letters: "Those letters don't stop on their own — but they can be stopped.",
  garnishment:
    "Garnishments and levies can often be paused or released once you act.",
  not_filed:
    "It doesn't matter how long it's been. There's no lecture waiting for you — just a path forward.",
  off_my_back: "One call is how that weight starts coming off.",
};

/* ==========================================================================
   Brand chrome
   ========================================================================== */

// Brand mark: red seal with white US-map motif (per official UTA logo).
const SealMark = React.memo(function SealMark() {
  return (
    <svg viewBox="0 0 40 40" className="h-9 w-9" aria-hidden="true">
      <circle cx="20" cy="20" r="19" fill="#b91c1c" />
      <circle cx="20" cy="20" r="19" fill="none" stroke="#7f1414" strokeWidth="1" />
      {/* Simplified contiguous-US silhouette */}
      <path
        d="M8.5 15.5 L14 14.2 L19 13.8 L24 14.2 L27.5 15.2 L30.5 15 L31.5 17 L30 19.5 L31 21.5 L29.5 24 L27 24.5 L25.5 27.5 L24 26 L21 26.5 L17.5 25.8 L14.5 26.5 L12.5 24.5 L10.5 23.5 L9.5 20.5 L8 18 Z"
        fill="#ffffff"
        opacity="0.95"
      />
    </svg>
  );
});

const TrustBar = React.memo(function TrustBar() {
  return (
    <div className="sticky top-0 z-40 border-b border-gold/30 bg-[#081b33] px-3 py-2 text-center text-[11px] font-semibold tracking-[0.06em] text-white/90">
      <span>BBB A+ Rated</span>
      <span className="mx-2 text-gold">·</span>
      <span>Licensed Tax Attorneys</span>
      <span className="mx-2 text-gold">·</span>
      <span>$500M+ Resolved</span>
    </div>
  );
});

const Masthead = React.memo(function Masthead() {
  return (
    <div className="flex items-center justify-center gap-2.5 bg-navy px-5 pb-1 pt-4">
      <SealMark />
      <p className="font-serif text-[17px] font-bold tracking-[0.01em] text-white">
        United Tax Attorneys
      </p>
    </div>
  );
});

const Footer = React.memo(function Footer() {
  return (
    <footer className="mt-12 bg-[#081b33] px-5 pb-8 pt-7">
      <div className="mx-auto max-w-md text-center">
        <p className="font-serif text-sm font-bold tracking-[0.06em] text-white/80">
          UNITED TAX ATTORNEYS
        </p>
        {/* TODO before launch: add the firm's real street address, phone-hours
            line, and state bar / licensing line here. Real, verifiable firm
            details are the single strongest credibility signal on this page. */}
        <div className="mx-auto mt-3 h-px w-16 bg-gold/50" />
        <p className="mt-4 text-[11px] leading-relaxed text-slate-400">
          Individual results vary. Not all applicants will qualify. United Tax
          Attorneys is not affiliated with the IRS or any government agency.
          Consultations are free; any service fees are disclosed before
          engagement. © 2026 United Tax Attorneys.
        </p>
      </div>
    </footer>
  );
});

/* ==========================================================================
   Quiz building blocks
   ========================================================================== */

function ProgressBar({ step }) {
  return (
    <div className="flex gap-1.5">
      <div className={"h-1 flex-1 rounded-full " + (step >= 1 ? "bg-gold" : "bg-slate-200")} />
      <div className={"h-1 flex-1 rounded-full " + (step >= 2 ? "bg-gold" : "bg-slate-200")} />
    </div>
  );
}

function OptionCard({ label, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="group flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-[15px] text-left text-[15px] font-semibold leading-snug text-navy shadow-[0_1px_3px_rgba(11,37,69,0.08)] transition-all duration-150 hover:border-gold active:-translate-y-0.5 active:border-gold active:shadow-[0_6px_16px_rgba(11,37,69,0.14)]"
    >
      <span>{label}</span>
      <svg
        viewBox="0 0 20 20"
        className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-gold group-active:text-gold"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M7 4l6 6-6 6" />
      </svg>
    </button>
  );
}

const LockIcon = React.memo(function LockIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="h-3.5 w-3.5 shrink-0 text-slate-400"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <rect x="4.5" y="9" width="11" height="7.5" rx="1.5" />
      <path d="M7 9V6.5a3 3 0 0 1 6 0V9" />
    </svg>
  );
});

/* ==========================================================================
   Screen 1 — Hero + Question 1
   ========================================================================== */

function Screen1({ onAnswer }) {
  return (
    <div className="screen-in">
      <div className="relative overflow-hidden">
        {/* Photo hero: law-office image under a deep navy wash keeps text legible */}
        <img
          src="img/hero-office.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/95 via-navy/80 to-navy" />
        <div className="relative">
          <Masthead />
          <div className="px-5 pb-16 pt-5 text-center">
            <h1 className="mx-auto max-w-md font-serif text-[21px] font-bold leading-[1.4] text-white">
              Behind on Taxes? Find Out Where You Stand in 10 Seconds.
            </h1>
            <div className="mx-auto mt-3.5 h-[3px] w-14 rounded-full bg-gold" />
            <p className="mx-auto mt-3.5 max-w-md text-[13px] leading-relaxed text-slate-300">
              Over 11 million Americans owe back taxes. Answer 2 quick
              questions to see if you qualify for the 2026 IRS Fresh Start
              Program.
            </p>
          </div>
        </div>
      </div>

      <div className="relative z-10 -mt-10 px-4">
        <div className="mx-auto max-w-md rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_16px_40px_rgba(11,37,69,0.18)]">
          <ProgressBar step={1} />
          <p className="mt-3.5 text-[14px] font-bold leading-snug text-navy">
            Millions of Americans owe back taxes. Which range are you in?
          </p>

          <div className="mt-3 space-y-2">
            {Q1_OPTIONS.map((opt) => (
              <OptionCard
                key={opt.id}
                label={opt.label}
                onSelect={() => onAnswer(opt)}
              />
            ))}
          </div>

          <p className="mt-3.5 text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Question 1 of 2
          </p>
          <p className="mt-1.5 flex items-center justify-center gap-1.5 text-center text-xs text-slate-500">
            <LockIcon />
            <span>Anonymous — no name, email, or SSN required.</span>
          </p>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   Screen 2 — Question 2
   ========================================================================== */

function Screen2({ onAnswer }) {
  return (
    <div className="screen-in">
      <Masthead />
      <div className="bg-navy px-5 pb-16 pt-5 text-center">
        <h2 className="mx-auto max-w-md font-serif text-[20px] font-bold leading-[1.4] text-white">
          What worries you most right now?
        </h2>
        <div className="mx-auto mt-3 h-[3px] w-14 rounded-full bg-gold" />
      </div>

      <div className="relative z-10 -mt-10 px-4">
        <div className="mx-auto max-w-md rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_16px_40px_rgba(11,37,69,0.18)]">
          <ProgressBar step={2} />

          <div className="mt-3.5 space-y-2">
            {Q2_OPTIONS.map((opt) => (
              <OptionCard
                key={opt.id}
                label={opt.label}
                onSelect={() => onAnswer(opt)}
              />
            ))}
          </div>

          <p className="mt-3.5 text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Question 2 of 2
          </p>
          <p className="mt-1.5 flex items-center justify-center gap-1.5 text-center text-xs text-slate-500">
            <LockIcon />
            <span>Your answers are confidential and never shared.</span>
          </p>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   Spinner — presentation only; claims nothing beyond "reviewing your answers"
   ========================================================================== */

function ReviewingScreen() {
  return (
    <div className="screen-in">
      <Masthead />
      <div className="bg-navy px-5 pb-16 pt-5" />
      <div className="relative z-10 -mt-10 px-4">
        <div className="mx-auto flex max-w-md flex-col items-center rounded-2xl border border-slate-100 bg-white px-4 py-14 shadow-[0_16px_40px_rgba(11,37,69,0.18)]">
          <div className="spinner h-10 w-10 rounded-full border-[3px] border-slate-200 border-t-gold" />
          <p className="mt-4 text-[15px] font-semibold text-navy">
            Reviewing your answers…
          </p>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   Screen 3 — Result (the conversion screen)
   ========================================================================== */

const BBBBadge = React.memo(function BBBBadge() {
  return (
    <svg viewBox="0 0 64 64" className="h-11 w-11" aria-label="BBB A+ Rated">
      <circle cx="32" cy="32" r="30" fill="#0b2545" stroke="#c9a227" strokeWidth="2" />
      <text
        x="32"
        y="29"
        textAnchor="middle"
        fill="#c9a227"
        fontSize="14"
        fontWeight="bold"
        fontFamily="Georgia, serif"
      >
        BBB
      </text>
      <text
        x="32"
        y="46"
        textAnchor="middle"
        fill="#ffffff"
        fontSize="14"
        fontWeight="bold"
        fontFamily="Georgia, serif"
      >
        A+
      </text>
    </svg>
  );
});

/* Objection handling for fence-sitters — placed after testimonials.
   DRAFT COPY: review before launch; keep "may/can" framing. */
const FAQS = [
  {
    q: "Does the call cost anything?",
    a: "No. The consultation is free, and there's no obligation to hire anyone. If a service can help you, any fees are explained clearly before you commit to anything.",
  },
  {
    q: "Will calling get me in trouble with the IRS?",
    a: "No. Your call is a private conversation with a tax professional — it is never shared with the IRS or anyone else. Nothing happens to your case unless you decide to move forward.",
  },
  {
    q: "What if I haven't filed in years?",
    a: "That's one of the most common situations we hear. Unfiled years can usually be brought current, and getting compliant is often the first step toward relief options.",
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-[0_1px_4px_rgba(11,37,69,0.05)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left text-[14px] font-semibold text-navy"
        aria-expanded={open}
      >
        <span>{q}</span>
        <span
          className={
            "shrink-0 text-gold transition-transform duration-150 " +
            (open ? "rotate-45" : "")
          }
          aria-hidden="true"
        >
          +
        </span>
      </button>
      {open && (
        <p className="px-4 pb-4 text-[13px] leading-relaxed text-slate-600">
          {a}
        </p>
      )}
    </div>
  );
}

const PrivilegeStrip = React.memo(function PrivilegeStrip() {
  const items = [
    "Attorney-Client Privilege",
    "100% Confidential",
    "Licensed Tax Attorneys",
  ];
  return (
    <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 border-t border-slate-100 pt-4">
      {items.map((label) => (
        <span
          key={label}
          className="flex items-center gap-1 whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-3 w-3 shrink-0 text-gold"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M4 12.5l5 5L20 6.5" />
          </svg>
          {label}
        </span>
      ))}
    </div>
  );
});

const Stars = React.memo(function Stars() {
  return (
    <span className="text-[13px] tracking-[0.1em] text-gold" aria-hidden="true">
      ★★★★★
    </span>
  );
});

function Testimonial({ initials, photo, quote, author }) {
  const [imgOk, setImgOk] = useState(true);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-[0_2px_10px_rgba(11,37,69,0.06)]">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 shrink-0 overflow-hidden rounded-full bg-navy ring-2 ring-gold/40">
          {photo && imgOk ? (
            <img
              src={photo}
              alt={author}
              loading="lazy"
              className="h-full w-full object-cover"
              onError={() => setImgOk(false)}
            />
          ) : (
            <span className="flex h-full w-full items-center justify-center font-serif text-sm font-bold text-gold">
              {initials}
            </span>
          )}
        </div>
        <div>
          <Stars />
          <p className="mt-1 text-sm leading-relaxed text-slate-700">
            “{quote}”
          </p>
          <p className="mt-1.5 text-xs font-semibold text-slate-500">
            — {author}
          </p>
        </div>
      </div>
    </div>
  );
}

function ResultScreen({ q1, q2 }) {
  return (
    <div className="screen-in bg-[#f7f6f2]">
      <Masthead />
      <div className="bg-navy pb-12" />

      <div className="relative z-10 -mt-10 px-4 pb-24">
        <div className="mx-auto max-w-md">
          <div className="rounded-2xl border border-slate-100 bg-white px-5 pb-7 pt-6 text-center shadow-[0_16px_40px_rgba(11,37,69,0.14)]">
            <h2 className="font-serif text-[21px] font-bold leading-[1.4] text-navy">
              ✅ Good News — Based on Your Answers, You May Qualify for Tax
              Relief.
            </h2>

            <p className="mt-4 text-[15px] leading-relaxed text-slate-700">
              {Q1_RESULT_LINES[q1]}
            </p>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-700">
              {Q2_EMPATHY_LINES[q2]}
            </p>
          </div>

          {/* Highest-leverage element: make the call feel safe and known. */}
          <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_2px_12px_rgba(11,37,69,0.08)]">
            {/* The person on the other end of the line — pairs with the first bullet */}
            <img
              src="img/attorney.jpg"
              alt="Licensed tax attorney"
              loading="lazy"
              className="h-44 w-full object-cover object-top"
            />
            <div className="border-t-4 border-gold bg-white px-5 pb-5 pt-4">
              <h3 className="font-serif text-[16px] font-bold leading-snug text-navy">
                Here's exactly what happens when you call:
              </h3>
              <ul className="mt-3.5 space-y-3">
                {[
                  "You talk to a licensed tax professional — not a salesperson",
                  "They ask a few questions about your situation (about 5 minutes)",
                  "You find out what options you actually have — even if the answer is “you don't need us”",
                  "No judgment. No pressure. Hang up anytime.",
                ].map((item, i) => (
                  <li
                    key={i}
                    className="flex gap-3 text-[15px] leading-snug text-slate-700"
                  >
                    <span className="mt-[1px] flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-navy/5">
                      <svg
                        viewBox="0 0 24 24"
                        className="h-3 w-3 text-gold"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M4 12.5l5 5L20 6.5" />
                      </svg>
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-gold/40 bg-white px-5 pb-5 pt-6 text-center shadow-[0_10px_30px_rgba(11,37,69,0.12)]">
            <PrimaryCallButton />
            <div className="mt-4">
              <PhoneNumberLink />
            </div>
            <p className="mt-2.5 flex items-center justify-center gap-1.5 text-xs font-medium text-slate-500">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <span>
                Free &amp; confidential · Open now — average wait under 1
                minute · No obligation
              </span>
            </p>
            <PrivilegeStrip />
          </div>

          <p className="mt-6 text-center text-[13px] leading-relaxed text-slate-600">
            Penalties and interest grow daily — but garnishments and levies can
            often be stopped once you act. Most people wish they'd called a
            year earlier.
          </p>

          <div className="mt-8 flex items-center justify-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3.5 shadow-[0_2px_10px_rgba(11,37,69,0.06)]">
            <BBBBadge />
            <div className="h-9 w-px bg-slate-200" />
            <div className="text-left text-xs font-semibold leading-relaxed text-slate-600">
              <p>10,000+ Americans helped</p>
              <p className="mt-0.5">
                <Stars /> <span className="align-middle">1,000+ 5-star reviews</span>
              </p>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <Testimonial
              initials="MR"
              photo="img/testimonial-mr.jpg"
              quote="I ignored it for three years. One call and they took over everything with the IRS. Settled $47,000 for a fraction of it."
              author="M.R., Ohio"
            />
            <Testimonial
              initials="DT"
              photo="img/testimonial-dt.jpg"
              quote="No lecture, no judgment. They just told me my options. Wish I'd called sooner."
              author="D.T., Texas"
            />
          </div>

          <p className="mt-3 text-center text-[10px] leading-relaxed text-slate-400">
            Photos are dramatizations, not actual clients or staff.
            Testimonials reflect individual experiences; results vary.
          </p>

          <div className="mt-7 space-y-2">
            {FAQS.map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>

          <p className="mt-7 flex items-center justify-center gap-1.5 text-center text-xs text-slate-500">
            <LockIcon />
            <span>
              Your call is confidential and never shared with the IRS or anyone
              else.
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   App — quiz state lives here, in React state only (no storage APIs)
   ========================================================================== */

export default function App() {
  // screen: "q1" | "q2" | "reviewing" | "result"
  const [screen, setScreen] = useState("q1");
  const [answers, setAnswers] = useState({ q1: null, q2: null });

  // Ref guard: StrictMode double-invokes effects in dev; quiz_start must fire once.
  const quizStartFired = useRef(false);
  useEffect(() => {
    if (quizStartFired.current) return;
    quizStartFired.current = true;
    track("quiz_start");
  }, []);

  // Ringba failsafe: keep every tel: href in sync with the swapped pool number.
  useEffect(() => installTelSync(), []);

  function answerQ1(opt) {
    track("quiz_step_1_answered", { answer: opt.label });
    setAnswers((a) => ({ ...a, q1: opt.id }));
    setScreen("q2");
    window.scrollTo(0, 0);
  }

  function answerQ2(opt) {
    track("quiz_step_2_answered", { answer: opt.label });
    setAnswers((a) => ({ ...a, q2: opt.id }));
    setScreen("reviewing");
    window.scrollTo(0, 0);
    setTimeout(() => {
      track("quiz_complete");
      setScreen("result");
    }, 1400);
  }

  return (
    <div className="desktop-shell min-h-screen sm:px-4 sm:py-10">
      <div className="mx-auto flex min-h-screen w-full max-w-[440px] flex-col bg-white sm:min-h-0 sm:overflow-hidden sm:rounded-2xl sm:shadow-[0_30px_80px_rgba(0,0,0,0.5)] sm:ring-1 sm:ring-white/10">
        <TrustBar />

        <main className="flex-1">
          {screen === "q1" && <Screen1 onAnswer={answerQ1} />}
          {screen === "q2" && <Screen2 onAnswer={answerQ2} />}
          {screen === "reviewing" && <ReviewingScreen />}
          {screen === "result" && (
            <ResultScreen q1={answers.q1} q2={answers.q2} />
          )}
        </main>

        <Footer />
      </div>

      {screen === "result" && <StickyCallBar />}
    </div>
  );
}
